import { useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { ExportMenu } from '../../../components/AppToolbar/ExportMenu';
import { CheckedCombo } from '../../../components/CheckedCombo';
import { useNotify } from '../../../components/notifications';
import { fetchPriceCodeCandidates } from './detailApi';
import { CHARGE_TYPE, type PriceCodeCandidate, type SampleMatrixOption } from './detailTypes';
import './quotes.css';

// ─────────────────────────────────────────────────────────────────────────────
// The "add price code" popup behind Analysis Pricing ▸ Add.
// Port of legacy `AnalysisPricing_DetailView_QuotePopup` (Model.xafml :2653):
// a Sample Matrix multi-select at the top and, beneath it, the grid
// `AnalysisPricing_ListView_QuotesPopup` — PriceCode · Sample Matrix · Matrix ·
// Test · IsGroup · Method · Component · ChargeType.
//
// The grid is the lab's TEST LIBRARY crossed with the chosen sample matrices,
// not a list of price codes: every test of those matrices appears, priced or
// not. A test with no price code cannot be ticked — legacy tracks those in
// `lstpricecodeempty` and suppresses their checkbox.
//
// SAME FLOW AS LEGACY (owner request 2026-10-20, "Follow the same flow"): every
// test is listed, priced or not, exactly as legacy lists them; ticking one with
// no price code is refused with legacy's own message (QuoteViewController
// selection callback, :8976-9030). The grid carries legacy's labelled
// "Export to" and its eight columns, and no search box (legacy has none; the
// column filter funnels remain). The earlier "Show tests without a price code"
// switch is gone.
//
// Rows already on the quote arrive pre-ticked (legacy :4149-4181), so OK with
// nothing changed adds nothing.
// ─────────────────────────────────────────────────────────────────────────────

const COLUMNS: AppColumn<PriceCodeCandidate>[] = [
  { field: 'priceCode',    header: 'PriceCode',     width: 110 },
  { field: 'sampleMatrix', header: 'SampleMatrix',  width: 140 },
  { field: 'matrix',       header: 'Matrix',        width: 110 },
  { field: 'test',         header: 'Test',          width: 200 },
  { field: 'isGroup',      header: 'IsGroup',       width: 80, align: 'center',
    render: (r) => <input type="checkbox" checked={r.isGroup} readOnly disabled aria-label="IsGroup" /> },
  { field: 'method',       header: 'Method',        width: 130 },
  { field: 'component',    header: 'Component',     width: 130 },
  {
    field: 'chargeTypeCode', header: 'ChargeType', width: 100,
    render: (r) => CHARGE_TYPE[r.chargeTypeCode] ?? '',
  },
];

/** How a row already on the quote is recognised among the candidates. Legacy's
 *  duplicate check is the test plus its component — NOT the sample matrix. */
export function candidateTestKey(c: {
  testId: string | null; componentId: string | null; isGroup: boolean; sampleMatrixId: string | null;
}): string {
  // An ordinary test is (test, component, SAMPLE MATRIX) — the nested-frame
  // check legacy runs on this form (QuoteViewController :1764). A group test is
  // (test) alone (:2141-2146). Mirrors QuotePricer on the server.
  if (c.isGroup) return `${(c.testId ?? '').toLowerCase()}||`;
  return [c.testId, c.componentId, c.sampleMatrixId].map((v) => (v ?? '').toLowerCase()).join('|');
}

interface Props {
  open: boolean;
  sampleMatrices: SampleMatrixOption[];
  /** `candidateTestKey` of every row already on the quote. */
  existingKeys: Set<string>;
  /** Sample matrices already used on the quote — pre-selected, as a convenience. */
  initialMatrixIds: string[];
  /** The newly ticked candidates only; rows already on the quote are left out. */
  onAdd: (picked: PriceCodeCandidate[]) => void;
  onClose: () => void;
}

export function PriceCodePickerDialog({
  open, sampleMatrices, existingKeys, initialMatrixIds, onAdd, onClose,
}: Props) {
  const notify = useNotify();
  const [matrixNames, setMatrixNames] = useState<string[]>([]);
  const [candidates, setCandidates]   = useState<PriceCodeCandidate[]>([]);
  const [loading, setLoading]         = useState(false);
  const [selected, setSelected]       = useState<Set<string | number>>(new Set());

  // Every opening starts clean, on the sample matrices the quote already uses.
  useEffect(() => {
    if (!open) return;
    setCandidates([]);
    setSelected(new Set());
    setMatrixNames(
      sampleMatrices.filter((m) => initialMatrixIds.includes(m.id)).map((m) => m.name ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const matrixIds = useMemo(
    () => sampleMatrices.filter((m) => matrixNames.includes(m.name ?? '')).map((m) => m.id),
    [sampleMatrices, matrixNames]);

  // Legacy refills the grid whenever Sample Matrix changes (:4009).
  useEffect(() => {
    if (!open) return;
    // Clearing the matrix while a load is still in flight cancels that load, and a
    // cancelled load never switches its own spinner off - so it is done here, or
    // the grid sits on "Loading" with OK disabled until the dialog is reopened.
    if (matrixIds.length === 0) { setCandidates([]); setSelected(new Set()); setLoading(false); return; }

    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const rows = await fetchPriceCodeCandidates(matrixIds);
        if (cancelled) return;
        setCandidates(rows);
        // Rows already on the quote arrive ticked (legacy :4172-4179).
        setSelected(new Set(rows.filter((r) => r.hasPriceCode && existingKeys.has(candidateTestKey(r)))
                                .map((r) => r.candidateKey)));
      } catch (e) {
        if (!cancelled) notify.error(e instanceof Error ? e.message : 'Failed to load the price codes.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, matrixIds.join(';')]);

  /** An unpriced test can never be ticked, however the selection changes —
   *  including through the header's select-all. Ticking one ON PURPOSE says
   *  why, in legacy's words; select-all drops them quietly. */
  function changeSelection(next: Set<string | number>) {
    const allowed = new Set(candidates.filter((c) => c.hasPriceCode).map((c) => c.candidateKey));
    const added = [...next].filter((k) => !selected.has(k));
    if (added.length === 1 && !allowed.has(String(added[0]))) {
      notify.warning('You are not allowed to select a test without Test Price Code.');
    }
    setSelected(new Set([...next].filter((k) => allowed.has(String(k)))));
  }

  function accept() {
    const ticked = candidates.filter((c) => selected.has(c.candidateKey));
    if (ticked.length === 0) {
      // Legacy wording, verbatim (QuoteViewController :2536).
      notify.info('Select atleast one checkbox.');
      return;
    }
    onAdd(ticked.filter((c) => !existingKeys.has(candidateTestKey(c))));
  }

  return (
    <AppDialog
      open={open}
      title="Analysis Pricing"
      width={1040}
      className="dlg-center qt-picker"
      maximizable
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" onClick={accept} disabled={loading}>
            OK
          </button>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div className="qt-picker-top">
        <label>Sample Matrix</label>
        <CheckedCombo
          options={sampleMatrices.map((m) => m.name ?? '')}
          values={matrixNames}
          onChange={setMatrixNames}
          placeholder="Select sample matrix…"
          ariaLabel="Sample Matrix"
          unit="sample matrices"
          summaryJoin="; "
          required
        />
      </div>

      <AppDataGrid<PriceCodeCandidate>
        rows={candidates}
        columns={COLUMNS}
        getRowId={(r) => r.candidateKey}
        loading={loading}
        selectable
        selectedIds={selected}
        onSelectionChange={changeSelection}
        getRowClassName={(r) => (r.hasPriceCode ? undefined : 'qt-unpriced')}
        headerLeft={<ExportMenu filename="Analysis Pricing" {...gridExportMatrix(candidates, COLUMNS)} triggerClassName="ca-btn" />}
        hideHeaderExport
        defaultPageSize={20}
        emptyText={matrixIds.length === 0 ? 'Choose a sample matrix to list its tests.' : 'No data to display'}
      />
    </AppDialog>
  );
}
